import {
  toWSDateString,
  WSActions,
  WSButton,
  WSCard,
  WSElement,
  WSFlexBox,
  WSInfoBox,
  WSText,
  WSTimeline,
  WSTimelineItemProps
} from "@wingspanhq/fe-component-library";
import type { AuditLogResource } from "@wingspanhq/wingspan-api-sdk";
import { ListAuditLogsFilterParameterResourceTypeEq } from "@wingspanhq/wingspan-api-sdk";
import startCase from "lodash/startCase";
import { useCallback, useMemo } from "react";
import { useQueryAuditLogsForResources } from "./useQueryAuditLogsForResources";
import { ErrorAlert } from "@components/ErrorAlert";
import { eventGroupToTimeline } from "./eventGroupToTimeline";
import { groupEvents } from "./groupEvents";
import { mapEventTitle } from "./mapEventTitle";
import { useModalResourceDrillDownHistory } from "./ModalResourceDrillDownHistory";

type Props = {
  refetchOnMount?: boolean;
  resources: AuditLogResource[];
  xWingspanAccount?: string;
  onViewAll?: () => void;
  drillDownResourceType?: ListAuditLogsFilterParameterResourceTypeEq;
  bare?: boolean;
};

/**
 * Resource types whose audit trail only starts at
 * {@link HISTORY_TRACKING_START_DATE} — a timeline containing one of them gets
 * a note saying so.
 *
 * v1's single `PayerPayeeEngagement` is two resources on v3 (the payer's and
 * the payee's side of the same relationship), so both are listed. v1's
 * `EngagementRequirementDefinition` has no v3 resource type at all and is
 * dropped — the audit log cannot be filtered on it, so it could never match.
 */
const NotBackfilledResourceTypes: ListAuditLogsFilterParameterResourceTypeEq[] =
  [
    ListAuditLogsFilterParameterResourceTypeEq.ENGAGEMENT,
    ListAuditLogsFilterParameterResourceTypeEq.REQUIREMENT_DEFINITION,
    ListAuditLogsFilterParameterResourceTypeEq.PAYER,
    ListAuditLogsFilterParameterResourceTypeEq.PAYEE,
    ListAuditLogsFilterParameterResourceTypeEq.PAYER_ENGAGEMENT,
    ListAuditLogsFilterParameterResourceTypeEq.PAYEE_ENGAGEMENT,
    ListAuditLogsFilterParameterResourceTypeEq.REQUIREMENT
  ];

const HISTORY_TRACKING_START_DATE = "7-11-2025";

const COMPACT_PAGE_SIZE = 4;
/** The audit-log page size is capped at 25 by the contract; a larger size 422s. */
const FULL_PAGE_SIZE = 25;

export const ResourceHistory: React.FC<Props> = props => {
  const refetchOnMount = props.refetchOnMount ?? true;
  const modalDrillDown = useModalResourceDrillDownHistory();

  const handleDrillDown = useCallback(
    (resource: AuditLogResource) => {
      modalDrillDown.open({
        resource,
        title: `${startCase(resource.resourceType)} history`,
        // The drill-down is this same timeline narrowed to one resource, so it
        // reads from the account this one did.
        xWingspanAccount: props.xWingspanAccount
      });
    },
    [modalDrillDown, props.xWingspanAccount]
  );

  /**
   * The resource *and* its related resources form one timeline. `listAuditLogs`
   * filters on a single resource, so the set is fanned out and merged
   * newest-first client-side — see `useQueryAuditLogsForResources`.
   */
  const queryAuditLogs = useQueryAuditLogsForResources(
    {
      resources: props.resources,
      pageSize: props.onViewAll ? COMPACT_PAGE_SIZE : FULL_PAGE_SIZE,
      xWingspanAccount: props.xWingspanAccount
    },
    { refetchOnMount }
  );

  const showNotAllBackfilledNote = useMemo(
    () =>
      props.resources.some(resource =>
        NotBackfilledResourceTypes.includes(resource.resourceType)
      ),
    [props.resources]
  );

  const eventGroups = useMemo(
    () =>
      groupEvents(
        (queryAuditLogs.data ?? []).map(event => ({
          ...event,
          title: mapEventTitle(event.title)
        }))
      ),
    [queryAuditLogs.data]
  );

  const timelineItems = useMemo<WSTimelineItemProps[]>(() => {
    const items = eventGroups.map(group =>
      eventGroupToTimeline(
        group,
        props.drillDownResourceType,
        props.drillDownResourceType ? handleDrillDown : undefined
      )
    );

    if (items.length === COMPACT_PAGE_SIZE && props.onViewAll) {
      return [
        ...items,
        {
          content: (
            <WSButton.Link onClick={props.onViewAll}>View all</WSButton.Link>
          ),
          avatar: {
            type: "icon",
            icon: "time"
          }
        }
      ];
    }

    return items;
  }, [
    eventGroups,
    props.onViewAll,
    props.drillDownResourceType,
    handleDrillDown
  ]);

  const content = queryAuditLogs.isSuccess ? (
    timelineItems.length ? (
      <WSTimeline items={timelineItems} />
    ) : (
      <WSFlexBox.Center p="L">
        <WSText.ParagraphSm color="gray400">No events yet</WSText.ParagraphSm>
      </WSFlexBox.Center>
    )
  ) : queryAuditLogs.isError ? (
    <WSElement p="M">
      <ErrorAlert context="Other" error={queryAuditLogs.error} />
    </WSElement>
  ) : (
    <WSElement shimmer pb="2XL">
      Loading history...
    </WSElement>
  );

  const backfillNote = showNotAllBackfilledNote ? (
    <WSInfoBox mt="M">
      <WSText.ParagraphXs color="gray500">
        Note: some history events tracking is only available from this date
        onward: {toWSDateString(HISTORY_TRACKING_START_DATE, "monthDayYear")}.
      </WSText.ParagraphXs>
    </WSInfoBox>
  ) : null;

  if (props.bare) {
    return (
      <WSElement>
        {content}
        {backfillNote}
      </WSElement>
    );
  }

  return (
    <WSElement>
      <WSFlexBox
        alignItems="center"
        justify="space-between"
        wrap="nowrap"
        mb="M"
      >
        <WSText weight="medium">
          {props.onViewAll ? "Recent history" : "History"}
        </WSText>
        {props.onViewAll &&
          queryAuditLogs.isSuccess &&
          timelineItems.length > 0 && (
            <WSActions
              buttons={[
                { kind: "Link", label: "View all", onClick: props.onViewAll }
              ]}
            />
          )}
      </WSFlexBox>

      <WSCard>
        {content}
        {backfillNote}
      </WSCard>
    </WSElement>
  );
};
